import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "../../Assets/svgs/AddIcon";
import KnowledgeBaseIcon from "../../Assets/svgs/KnowledgeBaseIcon";
import SolidTrashIcon from "../../Assets/svgs/SolidTrashIcon";
import { Loader } from "../../Common/Loader";
import MultiSelectDropdown from "../../Common/MultiSelectDropdown";
import useDebounce from "../../Common/useDebounce";
import useDidMountEffect from "../../Common/useDidMountEffect";
import { nameWithNumberValidation } from "../../Common/Validations";
import AddKnowledgeBaseModal from "../../Model/AddKnowledgeBaseModal";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import { setUnsavedChanges } from "../../Redux/Slice/Common/SelectedTabSlice";
import { getAssistantDocumentsList } from "../../Redux/Slice/Documents/AssistantDocumentListSlice";
import { deleteKnowledgeBase } from "../../Redux/Slice/KnowledgeBase/DeleteKnowledgeBaseSlice";
import { getKnowledgeBaseDetail } from "../../Redux/Slice/KnowledgeBase/GetKnowledgeBaseDetailSlice";
import { getKnowledgeBaseList } from "../../Redux/Slice/KnowledgeBase/GetKnowledgeBaseListSlice";
import { updateKnowledgeBase } from "../../Redux/Slice/KnowledgeBase/UpdateKnowledgeBaseSlice";
import { KnowledgeBaseListSidebar } from "./KnowledgeBaseListSidebar";

const CreateEditKnowledgeBase = () => {
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const dispatch = useDispatch();
  const INITIAL_FORMDATA = {
    knowledgeBaseName: "",
    documents: [],
  };

  const [formData, setFormData] = useState(INITIAL_FORMDATA);
  const [formError, setFormError] = useState({});
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedModel, setHasChangedModel] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);
  const { knowledgeBaseListData, loading } = useSelector(
    (state) => state?.knowledgeBaseList
  );
  const documentsListData = useSelector(
    (state) => state.documentList.documentsListData
  );

  const formattedKnowledgeBaseDocumentList = useMemo(() => {
    let _formattedDataList = Array.isArray(documentsListData?.data)
      ? documentsListData?.data?.map((assistant) => {
          return { value: assistant?.vapiFileId, label: assistant?.name };
        })
      : [];
    return _formattedDataList;
  }, [documentsListData, documentsListData?.data]);

  const knowledgeBaseList =
    knowledgeBaseListData?.data && Array.isArray(knowledgeBaseListData?.data)
      ? knowledgeBaseListData?.data
      : [];

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleChange = async (e) => {
    let { value, name } = e?.target;
    value = value.trimStart();
    const isValid = nameWithNumberValidation(value);
    if (isValid || value.trim() === "") {
      setFormError({ ...formError, knowledgeBaseNameError: "" });
    } else {
      setFormError({
        ...formError,
        knowledgeBaseNameError: "Please enter valid group name",
      });
    }
    let capitalized = value.charAt(0).toString().toUpperCase();
    value = capitalized + value.substring(1);

    setFormData({
      ...formData,
      [name]: value,
    });
    setHasChanged(true);
    dispatch(setUnsavedChanges(true));
  };

  const updateKnowledgeBaseDetails = async () => {
    let body = {
      name: formData.knowledgeBaseName,
      fileIds: formData.documents,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(
        updateKnowledgeBase({
          id: selectedKnowledgeBase?.id,
          body,
        })
      );
      if (
        updateKnowledgeBase.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        showSuccessMessage("Knowledge Base Details Updated Successfully!", () =>
          refetchKnowledgeBaseData()
        );
      } else {
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const hasFormError = useMemo(() => {
    return Object.values(formError).some((error) => error);
  }, [formError]);

  const isDisabled = useMemo(() => {
    return !formData.knowledgeBaseName || !formData?.documents?.length;
  }, [formData]);

  const refetchKnowledgeBaseData = async () => {
    await dispatch(getKnowledgeBaseList());
    await getKnowledgeBaseDetailById(selectedKnowledgeBase?.id);
  };

  const onDelete = async () => {
    setConfirmModal(false);
    setIsLoading(true);
    const result = await dispatch(
      deleteKnowledgeBase(selectedKnowledgeBase?.id)
    );
    setIsLoading(false);
    if (
      deleteKnowledgeBase.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Knowledge Base Deleted Successfully!", () =>
        getKnowledgeBaseData()
      );
    } else {
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const getKnowledgeBaseDetailById = async (id) => {
    setIsLoading(true);
    try {
      const result = await dispatch(getKnowledgeBaseDetail(id));
      if (
        getKnowledgeBaseDetail.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setHasChanged(false);
        dispatch(setUnsavedChanges(false));
        const knowledgeBaseDetails = result?.payload?.data;
        if (knowledgeBaseDetails) {
          setFormData({
            ...INITIAL_FORMDATA,
            knowledgeBaseName: knowledgeBaseDetails?.name,
            documents: Array.isArray(knowledgeBaseDetails?.files)
              ? knowledgeBaseDetails?.files?.map((file) => file?.vapiFileId)
              : [],
          });
          setSelectedKnowledgeBase(knowledgeBaseDetails);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getKnowledgeBaseData = async () => {
    const result = await dispatch(getKnowledgeBaseList());
    if (
      getKnowledgeBaseList.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      const knowledgeBaseList =
        result?.payload?.data && Array.isArray(result?.payload?.data)
          ? result?.payload?.data
          : [];
      if (knowledgeBaseList?.length) {
        getKnowledgeBaseDetailById(knowledgeBaseList[0]?.id);
      }
    }
  };

  const changeSelection = (functionItem) => {
    getKnowledgeBaseDetailById(functionItem?.id);
  };

  const getDocumentsList = async () => {
    setIsLoading(true);
    await dispatch(
      getAssistantDocumentsList({
        searchText: "",
        page: 1,
        limit: 200,
        type: "dropdown",
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    getDocumentsList();
    getKnowledgeBaseData();
  }, []);

  useDidMountEffect(() => {
    dispatch(getKnowledgeBaseList({ keyword: searchQuery }));
  }, [searchQuery]);

  return (
    <div className="page__body">
      {(isLoading || loading) && <Loader />}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {createModalVisible ? (
        <AddKnowledgeBaseModal
          closePopup={() => setCreateModalVisible(false)}
          onSuccess={() => {
            setCreateModalVisible(false);
            showSuccessMessage("Knowledge Base added successfully!");
            getKnowledgeBaseData();
          }}
        />
      ) : null}
      {confirmModal ? (
        <ConfirmModel
          onAccept={onDelete}
          onReject={() => {
            setConfirmModal(false);
          }}
          title="Delete Knowledge Base!"
          message="Are you sure you want to Delete the Knowledge Base?"
        />
      ) : (
        ""
      )}
      {hasChangedModel ? (
        <ConfirmModel
          onAccept={() => {
            dispatch(setUnsavedChanges(false));
          }}
          yes={"Yes"}
          no={"No"}
          onReject={() => {
            setHasChangedModel(false);
          }}
          title="Update Changes!"
          message="Changes made will not be saved. Are you sure you want to leave the page?"
        />
      ) : null}
      {(knowledgeBaseList?.length || searchText || searchQuery) &&
      selectedKnowledgeBase ? (
        <div className="createAssistantMain h_100">
          <KnowledgeBaseListSidebar
            selectedKnowledgeBase={selectedKnowledgeBase}
            changeSelection={changeSelection}
            showSuccessMessage={showSuccessMessage}
            getKnowledgeBaseData={getKnowledgeBaseData}
            searchText={searchText}
            setSearchText={setSearchText}
            hasChanged={hasChanged}
          />
          {selectedKnowledgeBase ? (
            <div className="createAssistantMain__col h_100">
              <div className="createAssistantMain__card">
                <div className="createAssistantHeader createAssistantHeader--flexStart">
                  <div
                    className="createAssistantHeader__info"
                    style={{ width: "80%" }}
                  >
                    <div className="title">
                      <div className="createAssistantHeader__info__content">
                        <h1 className="title title--h3">
                          {selectedKnowledgeBase?.name}
                        </h1>
                      </div>
                    </div>
                  </div>
                  <div className="btn__group">
                    <button
                      className="btn btn__primary"
                      disabled={isDisabled || hasFormError}
                      onClick={updateKnowledgeBaseDetails}
                    >
                      Update
                    </button>
                    <button
                      className="btn btn__danger btn__outlined"
                      onClick={() => setConfirmModal(true)}
                    >
                      <SolidTrashIcon />
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div className="createAssistantMain__card createAssistantMain__card--second">
                <div className="createAssistantBody">
                  <div className="accordion">
                    <div className="createFormGrid">
                      <div className="createFormGrid__row">
                        <div className="formCol">
                          <div className="form__Field form__Field--full">
                            <label
                              for="knowledgeBaseName"
                              className="form__label form__label--flex"
                            >
                              <span>
                                Knowledge Base Name
                                <sup className="form__label__asterisk">*</sup>
                              </span>
                            </label>
                            <input
                              type="text"
                              id="knowledgeBaseName"
                              name="knowledgeBaseName"
                              value={formData?.knowledgeBaseName}
                              onChange={handleChange}
                              className="form__input form__input--sm"
                              placeholder="Knowledge Base Name"
                              autoComplete="off"
                            />
                            {formError?.knowledgeBaseNameError ? (
                              <div className="form__error">
                                {formError?.knowledgeBaseNameError}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="createFormGrid__row">
                        <div className="formCol">
                          <div className="form__Field form__Field--full">
                            <label
                              for="documents"
                              className="form__label form__label--flex"
                            >
                              <span>
                                Documents
                                <sup className="form__label__asterisk">*</sup>
                              </span>
                            </label>
                            <MultiSelectDropdown
                              id="documents"
                              name="documents"
                              isDisabled
                              options={formattedKnowledgeBaseDocumentList}
                              selectedValue={formData.documents}
                              onChange={(docList) => {
                                setFormData({
                                  ...formData,
                                  documents: Array.from(
                                    docList,
                                    (option) => option?.value
                                  ),
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="createAssistant">
          <div className="createAssistant__card">
            <div className="createAssistant__details">
              <h2 className="title">Create knowledge base</h2>
              <div className="icon">
                <KnowledgeBaseIcon />
              </div>
              <p className="para">
                A knowledge base is the information your AI Assistant uses to
                understand and respond accurately. Add FAQs, documents, and key
                content so your Assistant can answer questions with context and
                precision.
              </p>
              <div className="btn__group">
                <button
                  className="btn btn__primary"
                  onClick={() => setCreateModalVisible(true)}
                >
                  <AddIcon />
                  Create knowledge base
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateEditKnowledgeBase;
